import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Upload, X, Check, ArrowLeft } from 'lucide-react';

const formSchema = z.object({
  image: z.any().refine((file) => {
    return file instanceof File && file.type.startsWith('image/');
  }, "por favor, selecione uma imagem válida"),
});

type FormValues = z.infer<typeof formSchema>;

const NEWSLETTER_CONFIG = {
  'thenews': {
    color: '#F9D029',
    logo: '/thenews.webp',
    title: 'dia mundial dos doguinhos'
  },
  'thebizness': {
    color: '#FF66C4',
    logo: '/thebizness.webp',
    title: 'upload de imagem'
  },
  'rising': {
    color: '#CFFCA4',
    logo: '/rising.webp',
    title: 'upload de imagem'
  },
  'goget': {
    color: '#FBAC5F',
    logo: '/goget.webp',
    title: 'upload de imagem'
  },
  'thechamps': {
    color: '#C72828',
    logo: '/thechamps.webp',
    title: 'upload de imagem'
  },
  'healthtimes': {
    color: '#579EDE',
    logo: '/healthtimes.webp',
    title: 'upload de imagem'
  },
  'thejams': {
    color: '#FFCF03',
    logo: '/thejams.webp',
    title: 'upload de imagem'
  },
  'pod-isso-vic': {
    color: '#F2A9DF',
    logo: '/podisso.webp',
    title: 'upload de imagem'
  },
  'thejobs': {
    color: '#000000',
    logo: '/thejobs.webp',
    title: 'upload de imagem'
  },
  'thestories': {
    color: '#FF66C4',
    logo: '/thestories.webp',
    title: 'upload de imagem'
  },
  'waffle': {
    color: '#FA2586',
    logo: '/logo.png',
    title: 'upload de imagem'
  }
} as const;

export function ImageUploadForm() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const newsletterParam = searchParams.get('n');
  const pastaParam = searchParams.get('p') || 'dogs';
  const newsletter = newsletterParam && NEWSLETTER_CONFIG[newsletterParam as keyof typeof NEWSLETTER_CONFIG] 
    ? newsletterParam 
    : 'thenews';
  const config = NEWSLETTER_CONFIG[newsletter as keyof typeof NEWSLETTER_CONFIG] || NEWSLETTER_CONFIG['thenews'];

  const [loading, setLoading] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);
  const [formStarted, setFormStarted] = useState(false);
  const [error, setError] = useState('');
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string>('');
  const [currentStep, setCurrentStep] = useState(0);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      image: null,
    },
    mode: "onSubmit"
  });

  // Add League Spartan font
  useEffect(() => {
    const link = document.createElement('link');
    link.href = 'https://fonts.googleapis.com/css2?family=League+Spartan:wght@300;400;500;600;700&display=swap';
    link.rel = 'stylesheet';
    document.head.appendChild(link);
    return () => {
      document.head.removeChild(link);
    };
  }, []);

  const handleImageSelect = (file: File) => {
    setSelectedImage(file);
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    form.setValue('image', file);
  };

  const handleRemoveImage = () => {
    setSelectedImage(null);
    setPreviewUrl('');
    form.setValue('image', null);
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
  };

  const handleNext = () => {
    if (selectedImage && currentStep === 0) {
      setCurrentStep(1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(0);
    }
  };

  const handleSubmit = async () => {
    if (!selectedImage) return;

    try {
      setLoading(true);
      setError('');

      const formData = new FormData();
      formData.append('image', selectedImage);
      formData.append('pasta', pastaParam);

      const response = await fetch('https://backend.testeswaffle.org/utils/upload/image', {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'erro ao fazer upload da imagem' }));
        
        if (errorData.error && errorData.analysis) {
          throw new Error(errorData.error);
        }
        
        throw new Error(errorData.message || 'erro ao fazer upload da imagem');
      }

      const result = await response.json();
      
      if (!result.success && result.error) {
        throw new Error(result.error);
      }

      setIsCompleted(true);
      form.reset();
      handleRemoveImage();

    } catch (error) {
      console.error('Erro:', error);
      setError(error instanceof Error ? error.message : 'ocorreu um erro ao fazer upload da imagem. por favor, tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setFormStarted(false);
    setIsCompleted(false);
    setSelectedImage(null);
    setPreviewUrl('');
    setCurrentStep(0);
    form.reset();
  };

  // Form Start Screen
  if (!formStarted) {
    return (
      <div className="bg-black text-white min-h-screen flex flex-col items-center justify-center" 
           style={{ fontFamily: "'League Spartan', sans-serif" }}>
        <motion.div
          initial={{ opacity: 0, y: 40, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="flex-grow flex flex-col justify-center items-center w-full max-w-4xl px-4"
        >
          <motion.img
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            src={config.logo}
            alt={config.title}
            className="w-48 mb-8"
          />
          
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-5xl md:text-6xl font-bold mb-6 text-center lowercase"
            style={{ color: config.color }}
          >
            {config.title}
          </motion.h1>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="bg-gray-900 rounded-lg p-8 mb-8 text-center max-w-2xl"
          >
            <h2 className="text-2xl md:text-3xl font-bold mb-4 lowercase" style={{ color: config.color }}>
              prazo encerrado
            </h2>
            <p className="text-lg md:text-xl text-gray-300 lowercase">
              o envio de imagens foi encerrado no dia 27 às 23:59
            </p>
            <p className="text-md md:text-lg text-gray-400 mt-4 lowercase">
              acompanhe{' '}
              <span
                onClick={() => navigate('/gallery/dogs')}
                className="cursor-pointer relative inline-block transition-colors hover:text-white"
                style={{ color: config.color }}
              >
                nossa galeria
                <span 
                  className="absolute bottom-0 left-0 w-full h-0.5 transition-transform duration-300 origin-left hover:scale-x-110"
                  style={{ backgroundColor: config.color }}
                />
              </span>
              {' '}para ver as imagens enviadas
            </p>
          </motion.div>
          
          <motion.button
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => navigate('/gallery/dogs')}
            className="text-black text-xl px-12 py-4 rounded-full shadow-2xl hover:scale-105 transform transition-all duration-300 font-semibold lowercase"
            style={{ 
              background: `linear-gradient(to right, ${config.color}, ${config.color}dd)`,
              boxShadow: `0 25px 50px -12px ${config.color}40`
            }}
          >
            ver galeria
          </motion.button>
        </motion.div>
      </div>
    );
  }

  // Form Completion Screen
  if (isCompleted) {
    return (
      <div className="bg-black text-white min-h-screen flex flex-col items-center justify-center"
           style={{ fontFamily: "'League Spartan', sans-serif" }}>
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
          className="text-center max-w-2xl px-4"
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ 
              type: "spring",
              stiffness: 260,
              damping: 20,
              delay: 0.1
            }}
            className="w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-8"
            style={{ backgroundColor: config.color }}
          >
            <Check className="w-12 h-12 text-black" />
          </motion.div>
          
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-4xl md:text-5xl font-bold mb-4 lowercase"
          >
            imagem enviada!
          </motion.h1>
          
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="text-xl text-gray-300 mb-8 lowercase"
          >
            sua imagem foi carregada com sucesso
          </motion.p>
          
          <motion.button
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={handleReset}
            className="text-black text-xl px-12 py-4 rounded-full shadow-2xl hover:scale-105 transform transition-all duration-300 font-semibold lowercase"
            style={{ 
              background: `linear-gradient(to right, ${config.color}, ${config.color}dd)`,
              boxShadow: `0 25px 50px -12px ${config.color}40`
            }}
          >
            enviar outra
          </motion.button>
        </motion.div>
      </div>
    );
  }

  // Main Form
  return (
    <div className="bg-black text-white min-h-screen flex flex-col items-center p-4"
         style={{ fontFamily: "'League Spartan', sans-serif" }}>
      {/* Header */}
      <div className="w-full max-w-3xl flex justify-between items-center py-4">
        <img src={config.logo} alt={config.title} className="h-12" />
        <button
          onClick={handleReset}
          className="text-gray-400 hover:text-white transition-colors"
        >
          <X className="w-6 h-6" />
        </button>
      </div>

      {/* Form Content */}
      <div className="flex-grow flex flex-col justify-center w-full max-w-3xl py-8">
        <AnimatePresence mode="wait">
          {currentStep === 0 ? (
            <motion.div
              key="upload"
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -50 }}
              transition={{ duration: 0.4, ease: "easeOut" }}
              className="mb-8"
            >
              <div className="mb-6">
                <span className="text-3xl font-bold" style={{ color: config.color }}>1</span>
                <h2 className="text-3xl font-bold mt-2 lowercase">selecione sua imagem</h2>
                <p className="text-lg text-gray-400 mt-2 lowercase">escolha uma foto para enviar</p>
              </div>

              {!selectedImage ? (
                <motion.div
                  whileHover={{ scale: 1.02 }}
                  className="border-2 border-dashed rounded-lg p-16 text-center transition-all duration-300 cursor-pointer"
                  style={{ borderColor: `${config.color}66` }}
                  onClick={() => document.getElementById('image-upload')?.click()}
                >
                  <Upload className="w-16 h-16 mx-auto mb-4" style={{ color: config.color }} />
                  <p className="text-xl mb-4 lowercase">arraste uma imagem ou clique para selecionar</p>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        handleImageSelect(file);
                      }
                    }}
                    className="hidden"
                    id="image-upload"
                  />
                </motion.div>
              ) : (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="relative"
                >
                  <img
                    src={previewUrl}
                    alt="Preview"
                    className="w-full max-w-md mx-auto rounded-lg"
                    style={{ boxShadow: `0 20px 40px ${config.color}20` }}
                  />
                  <button
                    onClick={handleRemoveImage}
                    className="absolute -top-3 -right-3 w-10 h-10 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-all duration-300 hover:scale-110"
                  >
                    <X className="w-5 h-5" />
                  </button>
                  <p className="text-center text-gray-400 mt-4 lowercase">{selectedImage.name}</p>
                </motion.div>
              )}
            </motion.div>
          ) : (
            <motion.div
              key="confirm"
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -50 }}
              transition={{ duration: 0.4, ease: "easeOut" }}
              className="mb-8"
            >
              <div className="mb-6">
                <span className="text-3xl font-bold" style={{ color: config.color }}>2</span>
                <h2 className="text-3xl font-bold mt-2 lowercase">confirmar envio</h2>
                <p className="text-lg text-gray-400 mt-2 lowercase">revise sua imagem antes de enviar</p>
              </div>

              <div className="bg-gray-900 rounded-lg p-6">
                <img
                  src={previewUrl}
                  alt="Preview"
                  className="w-full max-w-sm mx-auto rounded-lg mb-4"
                />
                <p className="text-center text-gray-300 lowercase">{selectedImage?.name}</p>
                <p className="text-center text-gray-500 text-sm mt-2 lowercase">
                  {selectedImage && `${(selectedImage.size / 1024 / 1024).toFixed(2)} mb`}
                </p>
              </div>

              {loading && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="mt-6 text-center"
                >
                  <div className="w-8 h-8 border-4 border-t-transparent rounded-full animate-spin mx-auto"
                       style={{ borderColor: config.color }} />
                  <p className="mt-3 text-gray-400 lowercase">enviando imagem...</p>
                </motion.div>
              )}
            </motion.div>
          )}
        </AnimatePresence>

        {error && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-red-500 text-center mb-6 lowercase"
          >
            {error}
          </motion.div>
        )}

        {/* Navigation */}
        <div className="flex justify-between items-center mt-8">
          {currentStep > 0 ? (
            <button
              onClick={handlePrevious}
              className="p-2 rounded-full hover:bg-gray-800 transition-colors"
            >
              <ArrowLeft className="w-6 h-6" />
            </button>
          ) : (
            <div />
          )}

          {currentStep === 0 && selectedImage ? (
            <button
              onClick={handleNext}
              className="text-black text-xl px-8 py-3 rounded-full shadow-2xl hover:scale-105 transform transition-all duration-300 font-semibold lowercase ml-auto"
              style={{ 
                background: `linear-gradient(to right, ${config.color}, ${config.color}dd)`,
                boxShadow: `0 20px 40px -12px ${config.color}40`
              }}
            >
              continuar
            </button>
          ) : currentStep === 1 ? (
            <button
              onClick={handleSubmit}
              disabled={loading}
              className="text-black text-xl px-8 py-3 rounded-full shadow-2xl hover:scale-105 transform transition-all duration-300 font-semibold lowercase ml-auto disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ 
                background: `linear-gradient(to right, ${config.color}, ${config.color}dd)`,
                boxShadow: `0 20px 40px -12px ${config.color}40`
              }}
            >
              {loading ? 'enviando...' : 'enviar'}
            </button>
          ) : null}
        </div>
      </div>

      {/* Footer Progress Bar */}
      <div className="w-full max-w-3xl pb-8">
        <div className="w-full h-1 bg-gray-700 rounded">
          <motion.div
            className="h-1 rounded"
            initial={{ width: '0%' }}
            animate={{ width: currentStep === 0 ? '50%' : '100%' }}
            transition={{ duration: 0.5, ease: "easeInOut" }}
            style={{ backgroundColor: config.color }}
          />
        </div>
        <p className="text-center text-gray-500 text-sm mt-2 lowercase">
          passo {currentStep + 1} de 2
        </p>
      </div>
    </div>
  );
}